import { MinusCircleIcon } from "@heroicons/react/24/outline";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, DeleteCommanWay, GetCommanWay, ListCommanWay, UpdateBookingCommanWay, UpdateCommanWay } from "API/commonApi";
import { AxiosResponse } from "axios";
import { InputTable } from "components/DataTable/InputTable";
import Modal from "components/Modal/Modal";
import { useFormik } from "formik";
import { debounce, includes } from "lodash";
import moment from "moment";
import { any } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import { roles } from "utils/constant";
import handleWheel from "utils/handleOnWheel";
import Spinner from "utils/Spinner";
import { AfDataType as ADT } from "utils/TableColumnData";
import { number, object, string } from "yup";
import { Button } from "@heroui/react";

// Modern compact select styles
const motherBagSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "36px",
    fontSize: "15px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 8px",
  }),
  input: (base: any) => ({
    ...base,
    margin: "0px",
    padding: "0px",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: "36px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "15px",
    padding: "8px 12px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "15px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#1e293b",
    fontSize: "15px",
  }),
};

const MInput = ({ label, name, type = "text", disabled = false, onBlurExtra, onKeyDown, className = "", value, onChange, onBlur }: any) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <label className="text-sm font-medium text-black-900 whitespace-nowrap min-w-[90px] text-right">{label}</label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      onBlur={(e) => {
        onBlur?.(e);
        onBlurExtra?.(e);
      }}
      onKeyDown={onKeyDown}
      disabled={disabled}
      placeholder={label}
      className={`flex-1 min-w-0 px-3 py-[6px] rounded-lg border text-sm transition-all duration-200 outline-none
        ${
          disabled
            ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
            : "bg-white border-slate-200 hover:border-primary2 focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
        } text-slate-700 placeholder:text-slate-400`}
    />
  </div>
);

const MSelect = ({ label, name, options, disabled = false, className = "", value, onChange, onBlur }: any) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <label className="text-sm font-medium text-slate-600 whitespace-nowrap min-w-[90px] text-right">{label}</label>
    <Select
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      isDisabled={disabled}
      styles={motherBagSelectStyles}
      placeholder={label}
      className="flex-1 min-w-0"
      menuPortalTarget={document.body}
    />
  </div>
);

type OptionType = {
  value: number;
  label: string;
};

interface MotherBagState {
  mode?: string;
  data?: { [key: string]: any }; // Adjust the type based on your data structure
  // Add other properties if needed
}

interface ValuesType {
  destination?: { value: string; label: string };
}

const AddMotherBag: React.FC = ({}) => {
  let isDisabled = false;
  const navigate = useNavigate();
  const { state } = useLocation();
  const { mode = "add", data = {} } = (state || {}) as MotherBagState; // Check for existence and type assertion

  const [newTableData, setNewTableData] = useState<any>([]);
  const [selectedTableData, setSelectedTableData] = useState<any>([]);

  const [savedAwbData, setSavedAwbData] = useState<any>([]);

  const [nestedTables, setNestedTables] = useState<any>({});
  const [pincodeSection, setPincodeSection] = useState<any>("");
  const [userDetails, setUserDetails] = useState<any>({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newMotherbagCode, setNewMotherbagCode] = useState("");

  const [branchCity, setBranchCity] = useState("");
  const [isFirstLoadDone, setIsFirstLoadDone] = useState<boolean>(false);

  const abortControllerRef = useRef(null);

  const handleDelete = (id) => {
    setNewTableData((prevData) => [...prevData, selectedTableData.find((d) => d.id === id)]);
    setSelectedTableData((prevData) => {
      return prevData.filter((d) => d?.id !== id);
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    window.location.reload();
  };

  useEffect(() => {
    console.log(data);
    resetForm();
    setNewTableData([]);
    setSelectedTableData([]);
  }, [mode]);

  const columnData = [
    {
      label: "NO.",
      key: "id",
      type: ADT.TEXT,
      initialValue: "",
      view: false,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Booking Date",
      key: "bookingDate",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "AWB NO.",
      key: "awb_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Weight",
      key: "weight",
      type: ADT.NUMBER,
      initialValue: 0,
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },

    {
      label: "Consignee",
      key: "consignee",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "Destination",
      key: "destination",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    mode == "view"
      ? {
          label: "Shipper City",
          key: "shipper_city",
          type: ADT.DB,
          db: "booking",
          db_col: "shipper_city",
          view: true,
        }
      : {},

    mode == "view"
      ? {
          label: "Shipper name",
          key: "shipper_sh",
          type: ADT.DB,
          db: "booking",
          db_col: "shipper_sh",
          view: true,
        }
      : {},
  ];

  const selectedColumnData = [
    {
      label: " ",
      key: "delete",
      type: ADT.BUTTON,
      btnComponent: (id) => (
        <>
          {mode === "view" ? null : (
            <button
              onClick={() => {
                handleDelete(id);
              }}
              type="button"
              className="rounded-md px-1 py-1 text-sm font-medium text-white bg-green-500 border border-gray-200 hover:bg-green-800 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
            >
              <MinusCircleIcon color="white" height={25} width={25} />
            </button>
          )}
        </>
      ),
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "NO.",
      key: "id",
      type: ADT.TEXT,
      initialValue: "",
      view: false,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Booking Date",
      key: "bookingDate",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "AWB NO.",
      key: "awb_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Weight",
      key: "weight",
      type: ADT.NUMBER,
      initialValue: 0,
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },

    mode == "view"
      ? {
          label: "Shipper name",
          key: "shipper_sh",
          type: ADT.DB,
          db: "booking",
          db_col: "shipper_sh",
          view: true,
        }
      : {},

    mode == "view"
      ? {
          label: "Shipper City",
          key: "shipper_city",
          type: ADT.DB,
          db: "booking",
          db_col: "shipper_city",
          view: true,
        }
      : {},

    {
      label: "Consignee",
      key: "consignee",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },

    {
      label: "Destination",
      key: "destination",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
  ];

  const savedColumnData = [
    {
      label: " ",
      key: "delete",
      type: ADT.BUTTON,
      btnComponent: (id) => (
        <>
          {mode === "view" ? null : (
            <button
              onClick={() => {
                //handleDelete(id);
                //setNewTableData((prevData) => [...prevData, selectedTableData.find((d) => d.id === id)]);
                setSavedAwbData((prevData) => {
                  return prevData.filter((d) => d?.id !== id);
                });
              }}
              type="button"
              className="rounded-md px-1 py-1 text-sm font-medium text-white bg-green-500 border border-gray-200 hover:bg-green-800 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
            >
              <MinusCircleIcon color="white" height={25} width={25} />
            </button>
          )}
        </>
      ),
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "NO.",
      key: "id",
      type: ADT.TEXT,
      initialValue: "",
      view: false,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Booking Date",
      key: "bookingDate",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
    {
      label: "AWB NO.",
      key: "awb_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Weight",
      key: "weight",
      type: ADT.NUMBER,
      initialValue: 0,
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },

    {
      label: "Consignee",
      key: "consignee",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },

    {
      label: "Destination",
      key: "destination",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: false,
      filter: true,
      sort: true,
    },
  ];

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(
    (destination = "") =>
      ListCommanWay(
        "/admin/awbdata/list",
        {
          .../*mode !== "edit" */ (typeof destination == "string" &&
            mode !== "view" && {
              destination: destination != "INTERNATIONAL" ? destination : undefined,
              prefix: destination == "INTERNATIONAL" ? ["I", "i"] : undefined,
              subbranch: values.subbranch.value,
              drs: { $eq: null },
              thirdPartyName: { $eq: null },
              assign_to_booking: { $ne: null },
              mother_bag_id: { $eq: null },
              includeReRoute: true,
              $or: [{ status: { $eq: null } }, { status: { $nin: [0, 99, 100] } }],
            }),
          ...(typeof destination != "string" &&
            (mode === "edit" || mode === "view") && {
              mother_bag_id: data.id,
              viewOnly: true,
            }),
        },
        { include: ["_assign_to_booking", "_mother_bag_id"], paginate: 5000 }
      ),
    {
      onSuccess: (data: any) => {
        console.log(data, data.status == "SUCCESS");
        if (data.status == "SUCCESS") {
          //UseToast("Data Added Successfully", "success");
          let cityName = data.data?.paginator?.city_name || "";

          let filteredAWBList;
          if (mode === "view" || mode === "edit") {
            filteredAWBList = data.data.data.filter((obj) => obj && (obj["mother_bag_id"] === 0 || obj["mother_bag_id"] === null));
          } else {
            filteredAWBList = data.data.data.filter(
              (obj) => obj && (obj.destination != cityName || obj["mother_bag_id"] === 0 || obj["mother_bag_id"] === null)
            );
            console.log([...filteredAWBList]);

            let finalFilteredData = [];
            filteredAWBList.forEach((f) => {
              let doAdd = true;

              for (let j = 0; j < selectedTableData.length; j++) {
                console.log(f.awb_no, selectedTableData[j].awb_no)
                if (f.awb_no == selectedTableData[j].awb_no) {
                  doAdd = false;
                  break;
                }
              }
              if(doAdd){
                finalFilteredData.push(f);
              }
            });
            filteredAWBList = finalFilteredData;
            console.log(filteredAWBList, selectedTableData, selectedColumnData);
          }

          if (filteredAWBList.length == 0) {
            setNewTableData([]);
          }

          if (filteredAWBList.length && /*mode !== "edit" &&*/ mode !== "view")
            setNewTableData((prevData) => {
              const newData = [];
              filteredAWBList.forEach((d) => {
                const obj: any = {};
                columnData.forEach((column) => {
                  obj[column["key"]] = d[column["key"]];
                  if (column["key"] === "bookingDate") {
                    //obj[column["key"]] = moment(d[column["key"]]).format("DD-MM-YYYY");
                    obj[column["key"]] =
                      d["_assign_to_booking"] && moment(d["_assign_to_booking"]?.date, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY");
                  }
                });
                let dontPush = false;
                try {
                  let shortAwbs = d._mother_bag_id?.shortAwbs?.split(",");
                  if (shortAwbs.includes(d.awb_no)) {
                    dontPush = true;
                  }
                } catch (c) {}
                if (!dontPush) {
                  newData.push(obj);
                }
              });

              return newData;
            });
          if (!isFirstLoadDone && /*mode === "edit" || */ mode === "view") {
            setSelectedTableData((prevData) => {
              const newData = [];
              data.data.data.forEach((d) => {
                const obj = {};
                columnData.forEach((column) => {
                  obj[column["key"]] = d[column["key"]];
                  if (column["key"] === "bookingDate") {
                    obj[column["key"]] =
                      d["_assign_to_booking"] && moment(d["_assign_to_booking"]?.date, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY");
                  }
                  if (column["key"] === "shipper_sh") {
                    obj[column["key"]] = d["_assign_to_booking"] && d["_assign_to_booking"][column["db_col"]];
                  }
                  if (column["key"] === "shipper_city") {
                    obj[column["key"]] = d["_assign_to_booking"] && d["_assign_to_booking"][column["db_col"]];
                  }
                });
                newData.push(obj);
              });
              console.log("===>setNewTableData", newData);

              return newData;
            });
          }

          if (!isFirstLoadDone && mode == "edit") {
            setSavedAwbData((prevData) => {
              const newData = [];
              data.data.data.forEach((d) => {
                const obj = {};
                columnData.forEach((column) => {
                  obj[column["key"]] = d[column["key"]];
                  if (column["key"] === "bookingDate") {
                    //obj[column["key"]] = moment(d[column["key"]]).format("DD-MM-YYYY");
                    obj[column["key"]] =
                      d["_assign_to_booking"] && moment(d["_assign_to_booking"]?.date, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY");
                  }
                });
                newData.push(obj);
              });
              return newData;
            });
          }

          setIsFirstLoadDone(true);
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const {
    mutate: mutateUpdateAWBs,
    isLoading: isUpdateAWBloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(
    (data) =>
      UpdateCommanWay({
        url: "/admin/awbdata/updateBulk",
        data: { data },
        query: {
          destination: values.destination.value,
        },
      }),
    {
      onSuccess: (data: any) => {
        console.log(data, data.status == "SUCCESS");
        if (data.status == "SUCCESS") {
          UseToast("Data Added Successfully", "success");
          setNewTableData([]);
          setSelectedTableData([]);
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const {
    mutate: mutateCreateMotherBag,
    isLoading: isCreateMotherBagloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(
    (data) =>
      CreateCommanWay({
        url: "/admin/mother_bag/create",
        data: {
          ...data,
          subbranch: data.subbranch.value,
          destination: data.destination.value,
          re_route_city: data.re_route_city?.value || null,
          third_party: data.third_party.value,
          delivery_subbranch: data.delivery_subbranch.value,
          refNo: data.refNo || null,
          totalNos: selectedTableData.length || 0,
          awb_no_ids: selectedTableData.map((a) => {
            return a.id;
          }),
        },
      }, abortControllerRef.current.signal),
    {
      onSuccess: (data: any) => {
        console.log(data, data.status == "SUCCESS");
        if (data.status == "SUCCESS") {
          setNewMotherbagCode(data.data.mother_bag_code);
          openModal();

          mutateUpdateAWBs(
            selectedTableData.map((d) => {
              console.log("======> d", {
                ...d,
                mother_bag_id: data.data.id,
              });

              return {
                ...d,
                mother_bag_id: data.data.id,
              };
            })
          );
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const {
    mutate: mutateUpdateMotherBag,
    isLoading: isUpdateMotherBagloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(
    (obj) =>
      UpdateCommanWay({
        url: "/admin/mother_bag/update/" + data.id,
        data: obj,
      }),
    {
      onSuccess: (data: any) => {
        console.log(data, data.status == "SUCCESS");
        if (data.status == "SUCCESS") {
          mutateUpdateAWBs(
            newTableData.map((d) => {
              console.log("======> d", { ...d, mother_bag_id: null });
              return {
                ...d,
                mother_bag_id: null,
              };
            })
          );
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const { handleSubmit, handleBlur, values, handleChange, errors, setValues, setErrors, touched, dirty, resetForm, setFieldValue } =
    useFormik({
      validationSchema: object().shape({}),
      initialValues: {
        subbranch: { value: 0, label: "" },
        destination: { value: "", label: "" },
        re_route_city: { value: "", label: "" },
        delivery_subbranch: { value: 0, label: "" },
        /*mother_bag_code: "MB" + Math.floor(Math.random() * 9000000),*/
        mother_bag_code: "",
        awb_no: "",
        refNo: "",
        third_party: { value: 0, label: "" },
      },
      onSubmit: async (values, { resetForm }) => {
        console.log("======> values", values);
        
        // Validate Sub Branch for admins
        const isAdminUser = Object.keys(userDetails).length > 0 && (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN);
        if (isAdminUser && (!values.subbranch?.value || values.subbranch?.value === 0)) {
          UseToast("Please select Sub Branch first", "error");
          return;
        }
        
        // Validate re_route_city (Outscan City) when NON-BRANCH is selected
        if (values.delivery_subbranch?.label === "NON-BRANCH" && !values.re_route_city?.value) {
          UseToast("Please select Outscan City when NON-BRANCH is selected", "error");
          return;
        }
        
        if (mode && mode === "edit") {
          let newAwbToAdd = [];
          let awbToRemote = [];

          mutateUpdateMotherBag({
            saved_awb_ids: savedAwbData.map((a: any) => {
              return a.id;
            }),
            selected_awb_ids: selectedTableData.map((a: any) => {
              return a.id;
            }),
            re_route_city: values.re_route_city?.value || null,
            refNo: values.refNo || null,
          });
          //navigate("/mother_bag/list");
          return;
        }

        if (selectedTableData.length == 0) {
          alert("No Airwaybill is Selected");
          return;
        }

        let obj: any = { ...values };
        obj.sysWeight = 0;
        selectedTableData.forEach((a) => {
          obj.sysWeight += a.weight;
        });
        obj.sysWeight = obj.sysWeight + "";
        console.log(selectedTableData);
        //return;
        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        mutateCreateMotherBag(obj);
      },
    });

  const modeActions = () => {
    if (mode) {
      if (data.destination) {
        mutate(data.destination);
      }

      if (mode === "edit") {
        Object.keys(data).map((obj) => {
          console.log(obj);
          setFieldValue(obj, data[obj]);

          if (obj === "subbranch") {
            setBranchOtp((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, data[obj]);

            values[obj] = data[obj];
          } else if (obj === "delivery_subbranch") {
            setDeliverySubbranchOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, data[obj]);
            values[obj] = data[obj];
          } else if (obj === "destination") {
            setDestinationOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, { value: data[obj], label: data[obj] });
            console.log("========> re_route_city", data[obj], data.subbranch);

            cityRefetch();
            values[obj] = data[obj];
          } else if (obj === "re_route_city") {
            setReRouteOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, { value: data[obj], label: data[obj] });
            console.log("========> re_route_city", data[obj], data.subbranch);

            cityRefetch();
          } else if (obj === "third_party") {
            setThirdPartyOtp((prevData) => {
              return data[obj];
            });
            values[obj] = data[obj];
          } else {
            values[obj] = data[obj];
          }
        });
      } else if (mode === "view") {
        Object.keys(data).map((obj) => {
          if (obj === "subbranch") {
            setBranchOtp(data[obj]);
            branchRefetch();
          } else if (obj === "delivery_subbranch") {
            setDeliverySubbranchOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, data[obj]);
            values[obj] = data[obj];
          } else if (obj === "destination") {
            setDestinationOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, { value: data[obj], label: data[obj] });
            console.log("========> re_route_city", data[obj], data.subbranch);

            cityRefetch();
          } else if (obj === "re_route_city") {
            setReRouteOpt((prevData) => {
              return data[obj];
            });
            setFieldValue(obj, { value: data[obj], label: data[obj] });
            console.log("========> re_route_city", data[obj], branchOpt);
            cityRefetch();
          } else if (obj === "third_party") {
            setThirdPartyOtp(data[obj]);
          } else {
            values[obj] = data[obj];
          }
        });
      }
    }
  };
  useEffect(() => {
    modeActions();
    cityRefetch();
  }, []);

  const [branchOpt, setBranchOtp] = useState<any>();

  const [branchOptions, setBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: branchError,
    isFetching: branchIsFetching,
    isError: branchIsError,
    isFetched: branchIsFetched,
    isLoading: branchIsLoading,
    refetch: branchRefetch,
  } = useQuery(["BranchesListApi"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.branch_name,
          });
        });
        setBranchOptions(opt);
        if (mode === "edit" || mode === "view") {
          const findBranch = opt.find((d) => d.value === branchOpt);
          if (findBranch) {
            setFieldValue("subbranch", {
              value: findBranch.value,
              label: findBranch.label,
            });
            values.subbranch = {
              value: findBranch.value,
              label: findBranch.label,
            };
          }
        }
      }
    },
  });

  const [thirdPartyOpt, setThirdPartyOtp] = useState<any>();

  const [thirdPartyOptions, setThirdPartyOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: thirdPartyError,
    isFetching: thirdPartyIsFetching,
    isError: thirdPartyIsError,
    isFetched: thirdPartyIsFetched,
    isLoading: thirdPartyIsLoading,
    refetch: thirdPartyRefetch,
  } = useQuery(["ThirdPartyListApi"], () => ListCommanWay("/admin/third_party/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.name,
          });
        });
        setThirdPartyOptions(opt);
        if (mode === "edit" || mode === "view") {
          const findThirdParty = opt.find((d) => d.value === thirdPartyOpt);
          if (findThirdParty) {
            setFieldValue("third_party", {
              value: findThirdParty.value,
              label: findThirdParty.label,
            });
            values.third_party = {
              value: findThirdParty.value,
              label: findThirdParty.label,
            };
          }
        }
      }
    },
  });

  const [destinationOpt, setDestinationOpt] = useState<any>();

  const [destinationOptions, setDestinationOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const { refetch: destCityRefetch } = useQuery(
    ["DestCityListApi"],
    () => ListCommanWay("/admin/city/list", {}, { paginate: 100000, isMotherbag: true }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt = [];
          if (data?.data) {
            Object.values(data?.data?.data)?.forEach((d: any) => {
              opt.push({
                value: d?.id,
                label: d?.city_name,
              });
            });
            setDestinationOptions(opt);
          }
        }
      },
    }
  );

  const { refetch: cityRefetch } = useQuery(["CityListApi"], () => ListCommanWay("/admin/city/list", {}, { paginate: 100000 }), {
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = [];
        if (data?.data) {
          Object.values(data?.data?.data)?.forEach((d: any) => {
            opt.push({
              value: d?.id,
              label: d?.city_name,
            });
          });
          setReRouteOptions(opt);

          if (mode === "edit" || mode === "view") {
            const findDestination = opt.find((d) => d.value === destinationOpt);
            if (findDestination) {
              setFieldValue("destination", { value: findDestination.value, label: findDestination.label });
            }
            const findReroute = opt.find((d) => d.value === reRouteOpt);
            if (findReroute) {
              setFieldValue("re_route_city", { value: findReroute.value, label: findReroute.label });
            }
          }
        }
      }
    },
  });

  const [reRouteOpt, setReRouteOpt] = useState<any>();
  const [reRouteOptions, setReRouteOptions] = useState<any>([{ value: 0, label: "Loading..." }]);
  const [deliverySubbranchOpt, setDeliverySubbranchOpt] = useState<any>();
  const [deliverySubbranchOptions, setDeliverySubbranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const { refetch: deliverySubbranchRefetch } = useQuery(
    ["deliverySubbranchAPI"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000, forOutscan: true }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt = [];
          data.data.data.forEach((p) => {
            opt.push({
              value: p.id,
              label: p.branch_name,
            });
          });
          // Sort to put NON-BRANCH first
          opt.sort((a, b) => {
            if (a.label === "NON-BRANCH") return -1;
            if (b.label === "NON-BRANCH") return 1;
            return 0;
          });
          setDeliverySubbranchOptions(opt);
          if (mode === "edit" || mode === "view") {
            console.log(deliverySubbranchOpt);
            const findBranch = opt.find((d) => d.value === deliverySubbranchOpt);
            if (findBranch) {
              setFieldValue("delivery_subbranch", {
                value: findBranch.value,
                label: findBranch.label,
              });
              values.delivery_subbranch = {
                value: findBranch.value,
                label: findBranch.label,
              };
            }
          }
        }
      },
    }
  );

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      console.log("userD.state", userD.state.user);
      if (userD.state.user.roleName !== roles.SUPER_ADMIN && userD.state.user.roleName !== roles.BRANCH_ADMIN) {
        setBranchOtp(userD.state.user.branch_name);
      }
    }
  }, []);

  useEffect(() => {
    cityRefetch();
  }, [branchOpt]);

  //console.log("-=======-----> newTableData", newTableData);

  const handleAWBNo = (awbNo) => {
    console.log(selectedTableData.find((d) => d.awb_no === awbNo));

    let found = false;
    newTableData.forEach((a) => {
      console.log(a.awb_no == awbNo, a.awb_no, awbNo);
      if (a.awb_no == awbNo) {
        found = true;
      }
    });

    if (found && awbNo && !selectedTableData.find((d) => d.awb_no === awbNo)) {
      setSelectedTableData((prevData) => [
        ...prevData,
        {
          ...newTableData.find((selectedAWB) => selectedAWB.awb_no === awbNo),
          mother_bag_id: values.mother_bag_code,
        },
      ]);
      setNewTableData((prevData) => {
        const findSelected = prevData.filter((obj) => obj.awb_no !== awbNo);
        return findSelected;
      });

      setFieldValue("awb_no", "");
    }
  };

  const getInput: any = (type, label, value, name, isDisabled, width) => {
    let ip = <div></div>;

    switch (type) {
      case ADT.NUMBER:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="mb-2 text-sm font-semibold text-gray-900">{label}</label>
            <input
              type="number"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  handleChange(e);
                }
              }}
              onWheel={handleWheel}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.TEXT:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="mb-2 text-sm font-semibold text-gray-900">{label}</label>

            <input
              type="text"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                name === "mother_bag_code" ? "bg-red-200" : name == "awb_no" ? "border-4 border-blue-500" : ""
              }`}
              placeholder={label}
              name={name}
              value={values[name]?.label !== undefined ? values[name]?.label : values[name]}
              onBlur={async (e) => {
                handleBlur(e);
                if (label === "Pincode") {
                  setPincodeSection(e.target.name.split("_")[0]);
                }
              }}
              onChange={(e) => {
                handleChange(e);
                if (name === "awb_no") {
                  handleAWBNo(e.target.value);
                }
              }}
              onKeyDown={disableEnterKey}
              disabled={mode && mode === "view" ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.DB:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="mb-2 text-sm font-semibold text-gray-900">{label}</label>
            <Select
              className={`${isDisabled ? "bg-gray-300" : ""} bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full `}
              options={value.all}
              onBlur={handleBlur}
              onChange={(e) => {
                if (name === "subbranch") {
                  console.log("=====> e.value", e.value);
                  setBranchOtp(e.value);
                  //destinationRefetch(e.value);
                }
                if (name === "destination") {
                  console.log(e);
                  mutate(e.label);
                }
                setFieldValue(name, e);
              }}
              name={name}
              value={values[name]}
              isDisabled={mode === "view" ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.TEXTAREA:
        break;
      case ADT.DATETIME:
        break;
      case ADT.DATE:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="mb-2 text-sm font-semibold text-gray-900">{label}</label>
            <input
              type="date"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.FILE:
        break;
      default:
        ip = <></>;
        break;
    }
    return ip;
  };

  const {
    mutate: mutateDeleteMotherBag,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => DeleteCommanWay({ url: "/admin/mother_bag/delete/" + data }), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        UseToast("Delete Success");
        navigate("/mother_bag/outscan");
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleDeleteMotherBag = (id: any) => {
    Swal.fire({
      title: "Are you sure!",
      icon: "warning",
      confirmButtonText: "Confirm Delete",
      showCancelButton: true,
    }).then((x) => {
      if (x.isConfirmed) {
        mutateDeleteMotherBag(id);
      }
    });
  };

  const disableEnterKey = (event) => {
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
    }
  };

  // Check if NON-BRANCH is selected
  const isNonBranchSelected = values.delivery_subbranch?.label === "NON-BRANCH";

  // Check if Sub Branch is visible and required (for admins)
  const isSubBranchRequired = Object.keys(userDetails).length > 0 && (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN);
  const isSubBranchMissing = isSubBranchRequired && (!values.subbranch?.value || values.subbranch?.value === 0);
  
  // Disable form in add mode until Sub Branch is selected (only for admins)
  const isFormDisabled = mode !== "edit" && mode !== "view" && isSubBranchMissing;

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 min-h-screen py-3 px-3 font-inter">
      {isCreateMotherBagloading && <Spinner />}

      {/* Success Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <h2 className="text-2xl font-bold mb-4">New Motherbag is added</h2>
        <p className="text-lg">Code: <span className="font-semibold text-primary">{newMotherbagCode}</span></p>
      </Modal>

      {/* ═══════════ HEADER BAR ═══════════ */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              {mode === "view" ? "View Mother Bag" : mode === "edit" ? "Edit Mother Bag" : "Mother Bag Tally"}
            </h1>
            {mode === "edit" && (
              <Button color="warning" size="sm" variant="flat" className="h-8 text-sm" onPress={() => navigate(-1)}>
                Cancel Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════ MAIN FORM ═══════════ */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        {/* ═══════════ CUSTOMER INFORMATION CARD ═══════════ */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl mb-3">
          <div className="pb-0 pt-3 px-4">
            <div className="flex items-center justify-between gap-3 w-full flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h2 className="text-base font-bold text-slate-800">Customer Information</h2>
              </div>
              {/* Mother Bag No - center between title and Sub Branch (only in edit/view mode) */}
              {(mode === "edit" || mode === "view") && (
                <MInput
                  label="Mother Bag No"
                  name="mother_bag_code"
                  value={values.mother_bag_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={true}
                  className="min-w-[250px] bg-red-50 border-2 border-red-300 rounded-xl p-1"
                />
              )}
              {/* Sub_branch for Admin only - beside title */}
              {isSubBranchRequired && (
                <div className="flex items-center gap-2">
                  <MSelect
                    label="Sub Branch *"
                    name="subbranch"
                    options={branchOptions}
                    value={values.subbranch}
                    onChange={(e: any) => {
                      setBranchOtp(e.value);
                      setFieldValue("subbranch", e);
                    }}
                    onBlur={handleBlur}
                    disabled={mode === "view"}
                    className={`min-w-[280px] ${isSubBranchMissing && mode !== "edit" && mode !== "view" ? "[&>div]:border-2 [&>div]:border-red-500 [&>div]:bg-red-50" : ""}`}
                  />
                  {isSubBranchMissing && mode !== "edit" && mode !== "view" && (
                    <span className="text-red-600 text-sm font-semibold whitespace-nowrap">⚠ Required</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <hr className="mt-2 border-slate-200" />
          <div className={`px-4 py-3 space-y-2 ${isFormDisabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
              <MSelect
                label="Destination"
                name="destination"
                options={destinationOptions}
                value={values.destination}
                onChange={(e: any) => {
                  setFieldValue("destination", e);
                  mutate(e.label);
                  // If Outscan Branch is not NON-BRANCH, sync re_route_city with destination
                  if (values.delivery_subbranch?.label !== "NON-BRANCH") {
                    setFieldValue("re_route_city", e);
                  }
                }}
                onBlur={handleBlur}
                disabled={mode === "view" || isFormDisabled}
              />
              <MSelect
                label="Outscan Branch"
                name="delivery_subbranch"
                options={deliverySubbranchOptions}
                value={values.delivery_subbranch}
                onChange={(e: any) => {
                  setFieldValue("delivery_subbranch", e);
                  // If not NON-BRANCH, set re_route_city same as destination
                  if (e.label !== "NON-BRANCH") {
                    setFieldValue("re_route_city", values.destination);
                  }
                }}
                onBlur={handleBlur}
                disabled={(mode && mode === "edit") || mode === "view" || isFormDisabled}
              />
              {/* Outscan City - only show when NON-BRANCH is selected */}
              {isNonBranchSelected && (
                <MSelect
                  label="Outscan City *"
                  name="re_route_city"
                  options={reRouteOptions}
                  value={values.re_route_city}
                  onChange={(e: any) => setFieldValue("re_route_city", e)}
                  onBlur={handleBlur}
                  disabled={mode === "view" || isFormDisabled}
                  className="border-2 border-orange-300 rounded-xl p-1 bg-orange-50"
                />
              )}
            </div>

            {/* Tag Id and Scan AWB No - centered */}
            <div className="grid grid-cols-3 justify-center gap-4">
              <MInput
                label="Tag Id"
                name="refNo"
                value={values.refNo}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={disableEnterKey}
                disabled={mode === "view" || isFormDisabled}
                className="min-w-[200px]"
              />
              <MInput
                label="Scan AWB No"
                name="awb_no"
                value={values.awb_no}
                onChange={(e: any) => {
                  handleChange(e);
                  handleAWBNo(e.target.value);
                }}
                onBlur={handleBlur}
                onKeyDown={disableEnterKey}
                disabled={mode === "view" || isFormDisabled}
                className="border-2 border-blue-400 rounded-xl p-1 bg-blue-50 min-w-[280px]"
              />
              {/* Hidden fields */}
              <div className="hidden">
                <MSelect
                  label="Third Party"
                  name="third_party"
                  options={thirdPartyOptions}
                  value={values.third_party}
                  onChange={(e: any) => setFieldValue("third_party", e)}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ ACTION BUTTONS ═══════════ */}
        <div className="flex justify-center gap-3 mb-3">
          {mode && mode === "view" ? (
            <>
              <Button
                color="danger"
                size="md"
                variant="solid"
                className="font-semibold"
                onPress={() => handleDeleteMotherBag(data.id)}
              >
                Delete
              </Button>
              <Button
                color="primary"
                size="md"
                variant="solid"
                className="font-semibold"
                onPress={() => navigate(-1)}
              >
                Go Back
              </Button>
            </>
          ) : mode && mode === "edit" ? (
            <Button
              type="submit"
              size="lg"
              className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-xl shadow-lg shadow-[#001f3f]/30 h-11 px-8 text-base tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all"
              isLoading={isUpdateMotherBagloading}
            >
              Update Mother Bag
            </Button>
          ) : (
            <Button
              type="submit"
              size="lg"
              className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-xl shadow-lg shadow-[#001f3f]/30 h-11 px-8 text-base tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all"
              isLoading={isCreateMotherBagloading}
            >
              Save Mother Bag
            </Button>
          )}
        </div>

        {/* ═══════════ AWB TABLES ═══════════ */}
        {mode !== "view" && (
          <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl mb-3 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2">
              <h3 className="text-white font-semibold text-base flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Total Airwaybill in Location ({newTableData.length})
              </h3>
            </div>
            <div className="p-3">
              <InputTable
                columnData={columnData}
                tableData={newTableData}
                setNewTableData={setNewTableData}
                customClasses="flex-1"
                mode={"view"}
              />
            </div>
          </div>
        )}

        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-xl mb-3 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-2">
            <h3 className="text-white font-semibold text-base flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Selected Airwaybill ({selectedTableData.length})
            </h3>
          </div>
          <div className="p-3">
            <InputTable
              columnData={selectedColumnData}
              tableData={selectedTableData}
              setNewTableData={setSelectedTableData}
              customClasses="flex-1"
              mode={"view"}
            />
          </div>
        </div>

        {mode === "edit" && (
          <div className="shadow-xl shadow-slate-200/50 bg-blue-100/80 backdrop-blur-sm rounded-xl mb-3 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-4 py-2">
              <h3 className="text-white font-semibold text-base flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Saved Airwaybill in Motherbag ({savedAwbData.length})
              </h3>
            </div>
            <div className="p-3">
              <InputTable
                columnData={savedColumnData}
                tableData={savedAwbData}
                setNewTableData={setSavedAwbData}
                customClasses="flex-1"
                mode={"view"}
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default AddMotherBag;
